import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { CREATE_EVENT_DAY, UPDATE_EVENT_DAY } from '@/modules/API/daycare/eventDayHandler.ts'
import moment from 'moment/moment'
import UserInput from '@/components/_custom/UserInput/userInput.tsx'
import { useTranslation } from 'react-i18next'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker.tsx'
import TimePickerComponent from '@/components/_custom/Picker/timePicker/timePicker.tsx'
import DateTimePickerComponent from '@/components/_custom/Picker/dateTimePicker/dateTimePicker.tsx'
import TextArea from '@/components/_custom/TextArea/textArea.tsx'
import { Section } from '@/types/Section.ts'
import { GET_SECTIONS } from '@/modules/API/daycare/sectionHandler.ts'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import { Daycare, DaycareType } from '@/types/Daycare.ts'
import styles from './editEventDay.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'
import { EventDay } from '@/types/EventDay.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { Delete } from '@mui/icons-material'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { Role } from '@/types/Role.ts'

interface EditEventDayProps {
    create: boolean
    daycareId: number
    daycareType?: DaycareType
    setTitle: (title: string) => void
    title: string
    setIsClosingDay?: (isClosingDay: boolean) => void
    isClosingDay?: boolean
    handleClose: () => void
    refetchAllEvents: () => Promise<void>
    event?: EventDay
    date: string
    setDate: (date: string) => void
}

const EditEventDay: FunctionComponent<EditEventDayProps> = ({
    create,
    daycareId,
    daycareType,
    handleClose,
    setTitle,
    title,
    setIsClosingDay,
    isClosingDay,
    refetchAllEvents,
    event,
    date,
    setDate
}: EditEventDayProps) => {
    const { user } = useAuth()

    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [startHour, setStartHour] = useState<string>(
        !create ? moment(event?.startHour, 'HH:mm:ss').format('HH:mm') : '08:00'
    )
    const [endHour, setEndHour] = useState<string>(
        !create ? moment(event?.endHour, 'HH:mm:ss').format('HH:mm') : '17:30'
    )
    const [message, setMessage] = useState<string>(event?.message || '')
    const [sections, setSections] = useState<string[]>(event?.sections.map((section) => section.toString()) || [])
    const [reservation, setReservation] = useState<boolean>(event?.reservation || false)

    const [places, setPlaces] = useState<string>(event?.places.toString() || '1')
    const [attendant, setAttendant] = useState<string>(event?.attendant.toString() || '1')
    const [requiredAttendant, setRequiredAttendant] = useState<boolean>(event?.attendantMin || false)

    const [parentOnly, setParentOnly] = useState<boolean>(event?.parentOnly || false)
    const [visibleToAll, setVisibleToAll] = useState<boolean>(event ? event.sections.length === 0 : false)

    const [releaseDate, setReleaseDate] = useState<string>(
        event?.releaseDate ? moment(event.releaseDate, 'DD-MM-YYYY HH:mm:ss').format('DD-MM-YYYY HH:mm') : ''
    )
    const [expiryDate, setExpiryDate] = useState<string>(
        event?.expiryDate ? moment(event.expiryDate, 'DD-MM-YYYY HH:mm:ss').format('DD-MM-YYYY HH:mm') : ''
    )

    const [selectedDaycare, setSelectedDaycare] = useState<string[]>([user?.currentDaycare?.id.toString() || ''])

    const daycareOptions: SelectOptionsDeprecated = [
        {
            options:
                user?.daycares.map((daycare: Daycare): SelectOptionDeprecated => {
                    return {
                        name: daycare.name,
                        value: daycare.id.toString()
                    }
                }) || []
        }
    ]

    async function createEventDay(): Promise<void> {
        if (create && user?.role === Role.trustee && selectedDaycare.length >= 1) {
            for (const daycare of selectedDaycare) {
                const sectionsByDaycare: Section[] | undefined = user.daycares.find(
                    (d: Daycare): boolean => d.id === Number(daycare)
                )?.sections
                const sectionIds: number[] = sectionsByDaycare
                    ? sectionsByDaycare.map((s: Section): number => s.id)
                    : []

                await createOneEventDay({
                    variables: {
                        daycare_id: Number(daycare),
                        title: title,
                        date: date,
                        start_hour: startHour,
                        end_hour: endHour,
                        message: message,
                        sections: visibleToAll ? [] : sectionIds,
                        reservation: reservation,
                        places: Number(places),
                        attendant: Number(attendant),
                        attendant_min: requiredAttendant,
                        parent_only: parentOnly,
                        release_date: releaseDate === '' ? undefined : releaseDate,
                        expiry_date: expiryDate === '' ? undefined : expiryDate
                    }
                })
            }
        } else {
            await createOneEventDay({
                variables: {
                    daycare_id: daycareId,
                    title: title,
                    date: date,
                    start_hour: startHour,
                    end_hour: endHour,
                    message: message,
                    sections: visibleToAll ? [] : sections.map(Number),
                    reservation: reservation,
                    places: Number(places),
                    attendant: Number(attendant),
                    attendant_min: requiredAttendant,
                    parent_only: parentOnly,
                    release_date: releaseDate === '' ? undefined : releaseDate,
                    expiry_date: expiryDate === '' ? undefined : expiryDate
                }
            })
        }
    }

    const [createOneEventDay, { loading: loadingCreate }] = useMutation(CREATE_EVENT_DAY, {
        onCompleted: () => {
            refetchAllEvents()
            setTitle('')

            showNotificationSuccess(t('eventCreated'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorCreatingEvent'))
        }
    })

    const [updateOneEventDay, { loading: loadingUpdate }] = useMutation(UPDATE_EVENT_DAY, {
        variables: {
            eventDay_id: event?.id,
            daycare_id: daycareId,
            title: title,
            date: date,
            start_hour: startHour,
            end_hour: endHour,
            message: message,
            sections: visibleToAll ? [] : sections.map((section) => Number(section)),
            reservation: reservation,
            places: Number(places),
            attendant: Number(attendant),
            attendant_min: requiredAttendant,
            parent_only: parentOnly,
            release_date: releaseDate === '' ? undefined : releaseDate,
            expiry_date: expiryDate === '' ? undefined : expiryDate
        },
        onCompleted: () => {
            refetchAllEvents()
            setTitle('')

            showNotificationSuccess(t('eventUpdated'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorUpdatingEvent'))
        }
    })

    const { data: sectionsData } = useQuery<{
        getSections: Section[]
    }>(GET_SECTIONS, {
        variables: {
            daycare_id: daycareId
        }
    })

    useEffect(() => {
        if (sectionsData && create && sectionsData.getSections.length > 0) {
            setSections(sectionsData.getSections.map((section: Section) => section.id.toString()))
        }
    }, [sectionsData])

    const selectOptionsSection: SelectOptionsDeprecated = [
        {
            options:
                sectionsData?.getSections.map((section) => {
                    return {
                        name: section.name.valueOf(),
                        value: section.id.toString()
                    }
                }) || []
        }
    ]

    useEffect(() => {
        if (event?.title) {
            setTitle(event.title)
        }
        if (event?.date) {
            setDate(moment(event.date, 'DD-MM-YYYY').format('DD-MM-YYYY'))
        }
    }, [])

    const initialVisibleToAll: boolean = event ? event.sections.length === 0 : false

    const disabled: boolean = create
        ? title === '' ||
          date === '' ||
          startHour === '' ||
          endHour === '' ||
          (!visibleToAll && sections.length === 0 && selectedDaycare.length === 0)
        : (title === event?.title &&
              date === moment(event?.date, 'DD-MM-YYYY').format('DD-MM-YYYY') &&
              startHour === moment(event?.startHour, 'HH:mm:ss').format('HH:mm') &&
              endHour === moment(event?.endHour, 'HH:mm:ss').format('HH:mm') &&
              message === event?.message &&
              visibleToAll === initialVisibleToAll &&
              sections.length === event?.sections.length &&
              sections.every((section) => event?.sections.includes(Number(section))) &&
              reservation === event?.reservation &&
              places === event?.places.toString() &&
              attendant === event?.attendant.toString() &&
              event?.parentOnly === parentOnly &&
              requiredAttendant === event?.attendantMin &&
              releaseDate === (event?.releaseDate ? moment(event.releaseDate).format('DD-MM-YYYY HH:mm') : '') &&
              expiryDate === (event?.expiryDate ? moment(event.expiryDate).format('DD-MM-YYYY HH:mm') : '')) ||
          loadingCreate ||
          loadingUpdate

    return (
        <div className={styles.Form}>
            <div className={styles.InputContainer}>
                <div className={styles.Input}>
                    <UserInput
                        label={t('title')}
                        value={title}
                        setValue={setTitle}
                        required
                        className={styles.UserInput}
                    />
                </div>
                {create && setIsClosingDay && (
                    <div className={styles.CheckBoxContainer}>
                        <Checkbox
                            text={t('isClosingDay')}
                            checked={isClosingDay}
                            onChange={() => setIsClosingDay(!isClosingDay)}
                        />
                    </div>
                )}
                <div className={styles.Input}>
                    <div className={styles.UserInput}>
                        <DatePickerComponent
                            value={date}
                            setValue={setDate}
                            label={t('date')}
                            className={styles.FullWidth}
                        />
                    </div>
                    {create && user?.role === Role.trustee ? (
                        <SelectMultiple
                            label={t('myDaycares')}
                            selectOptions={daycareOptions}
                            selectValues={selectedDaycare}
                            setSelectValues={setSelectedDaycare}
                            className={styles.UserInput}
                            maxToShow={5}
                        />
                    ) : (
                        <SelectMultiple
                            selectOptions={selectOptionsSection}
                            selectValues={sections}
                            setSelectValues={setSections}
                            className={styles.UserInput}
                            readOnly={visibleToAll}
                            label={
                                daycareType === DaycareType.rpe || daycareType === DaycareType.laep
                                    ? t('mySectors')
                                    : t('mySections')
                            }
                        />
                    )}
                </div>
                <div className={styles.Input}>
                    <div className={styles.UserInput}>
                        <TimePickerComponent
                            value={startHour}
                            setValue={setStartHour}
                            label={t('startHour')}
                            className={styles.FullWidth}
                        />
                    </div>
                    <div className={styles.UserInput}>
                        <TimePickerComponent
                            value={endHour}
                            setValue={setEndHour}
                            label={t('endHour')}
                            className={styles.FullWidth}
                        />
                    </div>
                </div>
                <div className={styles.Input}>
                    <TextArea
                        value={message}
                        className={styles.TextArea}
                        name={t('message')}
                        placeholder={t('eventDescription')}
                        onChange={setMessage}
                    />
                </div>
                <div className={styles.Input}>
                    <DateTimePickerComponent
                        value={releaseDate}
                        setValue={setReleaseDate}
                        label={t('releaseDate')}
                        allowEmpty
                        className={styles.UserInput}
                    />
                    {releaseDate !== '' && <Delete className={styles.Icon} onClick={() => setReleaseDate('')} />}
                </div>
                <div className={styles.CheckBoxInput}>
                    <Checkbox text={t('visibleAll')} checked={visibleToAll} onChange={setVisibleToAll} />
                </div>
                <div className={styles.CheckBoxInput}>
                    <Checkbox text={t('parentOnly')} checked={parentOnly} onChange={setParentOnly} />
                </div>
                <div className={styles.CheckBoxInput}>
                    <Checkbox text={t('activeReservation')} checked={reservation} onChange={setReservation} />
                </div>
                {reservation && (
                    <>
                        <div className={styles.Input}>
                            {!parentOnly && (
                                <UserInput
                                    label={t('numberChilds')}
                                    value={places}
                                    type='number'
                                    required
                                    fullWidth
                                    format={'num'}
                                    verify={'number'}
                                    maxNumber={150}
                                    minNumber={1}
                                    className={styles.UserInput}
                                    unallowedCharacters={'num'}
                                    setValue={setPlaces}
                                />
                            )}
                            <UserInput
                                label={t('numberAdults')}
                                value={attendant}
                                type='number'
                                required
                                fullWidth
                                format={'num'}
                                verify={'number'}
                                maxNumber={150}
                                minNumber={parentOnly ? 1 : 0}
                                className={styles.UserInput}
                                unallowedCharacters={'num'}
                                setValue={setAttendant}
                            />
                        </div>
                        {Number(attendant) > 0 && !parentOnly && (
                            <div className={styles.CheckBoxInput}>
                                <Checkbox
                                    checked={requiredAttendant}
                                    onChange={setRequiredAttendant}
                                    disabled={attendant === '0'}
                                    text={t('oneAdultRequired')}
                                />
                            </div>
                        )}
                        <div className={styles.Input}>
                            <DateTimePickerComponent
                                value={expiryDate}
                                setValue={setExpiryDate}
                                label={t('expiryDate')}
                                allowEmpty
                                minDate={releaseDate}
                                className={styles.UserInput}
                            />
                            {expiryDate !== '' && <Delete className={styles.Icon} onClick={() => setExpiryDate('')} />}
                        </div>
                    </>
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={create ? 'add' : 'update'}
                        onClick={create ? createEventDay : updateOneEventDay}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    )
}

export default EditEventDay
